import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

const getSession = () => {
  try {
    return JSON.parse(sessionStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

const scope = () => {
  const user = getSession();
  if (!user.client_id || !user.college_id) {
    throw new Error("Client or college session is missing. Please sign in again.");
  }
  return {
    clientId: encodeURIComponent(user.client_id),
    collegeId: encodeURIComponent(user.college_id),
    userEmail: encodeURIComponent(user.user_email),
    userId: encodeURIComponent(user.user_id),
  };
};

const request = async (config) => {
  const token = sessionStorage.getItem("token");
  const response = await axios({
    baseURL: API_BASE,
    ...config,
    headers: {
      ...(config.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  return response.data;
};

/**
 * API paths below are the proposed Student Master Management contract.
 * Keep the UI isolated from URL details here. Align these paths with your
 * Express routes before deploying.
 */
export const studentMasterApi = {
  getActiveBatches: () => {
    const { clientId, collegeId, userEmail, userId } = scope();
    return request({
      method: "GET",
      url: `/api/student-master/${clientId}/${collegeId}/batches`,
    });
  },

  getBatchBranches: (batchId, params = {}) => {
    const { clientId, collegeId } = scope();
    return request({
      method: "GET",
      url: `/api/student-master/${clientId}/${collegeId}/batches/${batchId}/branches`,
      params,
    });
  },

  getStudents: (selection, params = {}) => {
    const { clientId, collegeId } = scope();
    return request({
      method: "GET",
      url: `/api/student-master/${clientId}/${collegeId}/students`,
      params: {
        admittedbatch_id: selection.batchId,
        program_id: selection.programId,
        branch_id: selection.branchId,
        ...params,
      },
    });
  },

  getStudent: (studentId) => {
    const { clientId, collegeId } = scope();
    return request({
      method: "GET",
      url: `/api/student-master/${clientId}/${collegeId}/students/${studentId}`,
    });
  },

  saveStudent: (studentId, payload) => {
    const { clientId, collegeId, userEmail, userId } = scope();
    return request({
      method: studentId ? "PUT" : "POST",
      url: `/api/student-master/${clientId}/${collegeId}/students${
        studentId ? `/${studentId}` : ""
      }`,
      data: payload,
    });
  },

  deactivateStudent: (studentId) => {
    const { clientId, collegeId } = scope();
    return request({
      method: "DELETE",
      url: `/api/student-master/${clientId}/${collegeId}/students/${studentId}`,
    });
  },

  getEligibleAdmissions: (selection, params = {}) => {
    const { clientId, collegeId } = scope();
    return request({
      method: "GET",
      url: `/api/student-master/${clientId}/${collegeId}/eligible-admissions`,
      params: {
        admittedbatch_id: selection.batchId,
        program_id: selection.programId,
        branch_id: selection.branchId,
        ...params,
      },
    });
  },

  admitStudents: (selection, studentIds, seatTypeById = {}, newRollNoById = {}) => {
    const { clientId, collegeId, userEmail, userId } = scope();
    return request({
      method: "POST",
      url: `/api/student-master/${clientId}/${collegeId}/admit`,
      data: {
        admittedbatch_id: selection.batchId,
        program_id: selection.programId,
        branch_id: selection.branchId,
        student_ids: studentIds,
        seat_type_by_student_id: seatTypeById,
        new_rollno_by_student_id: newRollNoById,
        userEmail: userEmail,
        userId: userId,
      },
    });
  },

  changeProgram: (studentId, programId) => {
    const { clientId, collegeId } = scope();
    return request({
      method: "PATCH",
      url: `/api/student-master/${clientId}/${collegeId}/students/${studentId}/program`,
      data: { program_id: programId },
    });
  },

  changeBranch: (studentId, branchId) => {
    const { clientId, collegeId } = scope();
    return request({
      method: "PATCH",
      url: `/api/student-master/${clientId}/${collegeId}/students/${studentId}/branch`,
      data: { branch_id: branchId },
    });
  },
  validateStudentImport: (selection, rows) => {
    const { clientId, collegeId } = scope();
    return request({
      method: "POST",
      url: `/api/student-master/${clientId}/${collegeId}/students/import/validate`,
      data: {
        admittedbatch_id: selection.batchId,
        program_id: selection.programId,
        branch_id: selection.branchId,
        rows,
      },
    });
  },

  importStudents: (selection, rows) => {
    const { clientId, collegeId } = scope();
    return request({
      method: "POST",
      url: `/api/student-master/${clientId}/${collegeId}/students/import`,
      data: {
        admittedbatch_id: selection.batchId,
        program_id: selection.programId,
        branch_id: selection.branchId,
        rows,
      },
    });
  },

  uploadStudentPhotos: (selection, files) => {
    const { clientId, collegeId } = scope();
    const data = new FormData();
    data.append("admittedbatch_id", selection.batchId);
    data.append("program_id", selection.programId);
    data.append("branch_id", selection.branchId);
    files.forEach((file) => data.append("photos", file, file.name));
    return request({
      method: "POST",
      url: `/api/student-master/${clientId}/${collegeId}/students/photos/upload`,
      data,
    });
  },
  getLookups: () => {
    const { clientId, collegeId } = scope();
    return request({
      method: "GET",
      url: `/api/student-master/${clientId}/${collegeId}/lookups`,
    });
  },

  // Download students as CSV

  downloadStudents: async (selection) => {
    const { clientId, collegeId } = scope();

    const token = sessionStorage.getItem("token");

    const response = await axios.get(
      `${API_BASE}/api/student-master/${clientId}/${collegeId}/students/export`,
      {
        params: {
          batchId: selection.batchId,
          programId: selection.programId,
          branchId: selection.branchId,
        },
        responseType: "blob",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const safeName = (value) =>
      String(value || "Unknown")
        .replace(/[<>:"/\\|?*]/g, "_")
        .replace(/\s+/g, "_");

    const filename =
      [
        "Student_master",
        safeName(selection.batchName),
        safeName(selection.programName),
        safeName(selection.branchName),
      ].join("_") + ".xlsx";

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);

    return {
      success: true,
      filename,
    };
  },
};

export default studentMasterApi;
